import CatalogCategories from "@/pages/configuration/delay-categories";
import CatalogAdjustmentReason from "@/pages/configuration/pages/adjustment-reasons";
import CatalogAdjustmentType from "@/pages/configuration/pages/adjustment-types";
import HeatAgeThresholds from "@/pages/configuration/pages/heat-age-thresholds";
import InspectionTemplates from "@/pages/configuration/pages/inspection-templates";
import SurfaceDefects from "@/pages/configuration/pages/surface-defects";
import ShiftReportConfig from "@/pages/configuration/pages/shift-report-config";
import TensileRequirements from "@/pages/configuration/pages/tensile-requirements";
import TestsBySection from "@/pages/configuration/pages/tests-by-section";
import WaterMeterThresholds from "@/pages/configuration/pages/water-meter-thresholds";
import WaterAllocations from "@/pages/configuration/water-allocations";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CategoryIcon from "@mui/icons-material/Category";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import GridOnIcon from "@mui/icons-material/GridOn";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import LocationIcon from "@mui/icons-material/LocationOn";
import SpeedIcon from "@mui/icons-material/Speed";
import StorageIcon from "@mui/icons-material/Storage";
import TimelineIcon from "@mui/icons-material/Timeline";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import {
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	ListSubheader,
} from "@mui/material";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import CatalogDelayCodes from "../pages/configuration/delay-codes";
import CatalogLocation from "../pages/configuration/pages/locations";
import CatalogSectionFamily from "../pages/configuration/pages/section-families";
import Section from "../pages/configuration/pages/sections";
import { useAssetId } from "./hooks/useAssetId";
import {
	ConfigurationPath,
	isConfigurationAllowed,
} from "./mill-config/mill-extended-config";

export const configurationPath = "/configuration";

type SidebarButton = {
	path: ConfigurationPath;
	label: string;
	icon: ReactNode;
};

type SidebarSection = {
	name: string;
	buttons: SidebarButton[];
};

type Component = {
	path: ConfigurationPath | `${ConfigurationPath}/${string}`;
	main: () => ReactNode;
};

const sidebarSections: SidebarSection[] = [
	{
		name: "Mill Configuration",
		buttons: [
			{
				path: "section-families",
				label: "Section Families",
				icon: <StorageIcon />,
			},
			{ path: "sections", label: "Sections", icon: <StorageIcon /> },
			{ path: "locations", label: "Locations", icon: <LocationIcon /> },
		],
	},
	{
		name: "Threshold",
		buttons: [
			{
				path: "shift-report-config",
				label: "Threshold Adjustments",
				icon: <AssessmentIcon />,
			},
		],
	},
	{
		name: "Delays",
		buttons: [
			{ path: "delay-categories", label: "Categories", icon: <CategoryIcon /> },
			{ path: "delay-codes", label: "Codes", icon: <TimelineIcon /> },
		],
	},
	{
		name: "Adjustments",
		buttons: [
			{ path: "adjustments-types", label: "Types", icon: <CategoryIcon /> },
			{ path: "adjustments-reasons", label: "Reasons", icon: <StorageIcon /> },
		],
	},
	{
		name: "Lab",
		buttons: [
			{
				path: "tensile-requirements",
				label: "Tensile Requirements",
				icon: <FitnessCenterIcon />,
			},
			{
				path: "tests-by-section",
				label: "Tests by Section",
				icon: <FactCheckIcon />,
			},
			{
				path: "heat-age-thresholds",
				label: "Heat Age Thresholds",
				icon: <HourglassBottomIcon />,
			},
		],
	},
	{
		name: "Utilities",
		buttons: [
			{
				path: "water-allocations",
				label: "Water Allocations",
				icon: <WaterDropIcon />,
			},
			{
				path: "water-meter-thresholds",
				label: "Water Meter Thresholds",
				icon: <SpeedIcon />,
			},
		],
	},
	{
		name: "Inspections",
		buttons: [
			{
				path: "inspection-templates",
				label: "Templates",
				icon: <GridOnIcon />,
			},
			{
				path: "surface-defects",
				label: "Surface Defects",
				icon: <ReportProblemIcon />,
			},
		],
	},
];

const routeComponents: Component[] = [
	{ path: "delay-categories", main: () => <CatalogCategories /> },
	{ path: "delay-codes", main: () => <CatalogDelayCodes /> },
	{ path: "locations", main: () => <CatalogLocation /> },
	{ path: "sections", main: () => <Section /> },
	{ path: "sections/:sectionId", main: () => <Section /> },
	{ path: "section-families", main: () => <CatalogSectionFamily /> },
	{ path: "adjustments-types", main: () => <CatalogAdjustmentType /> },
	{ path: "adjustments-types/:typeId", main: () => <CatalogAdjustmentType /> },
	{ path: "adjustments-reasons", main: () => <CatalogAdjustmentReason /> },
	{
		path: "adjustments-reasons/:reasonId",
		main: () => <CatalogAdjustmentReason />,
	},
	{ path: "tensile-requirements", main: () => <TensileRequirements /> },
	{ path: "tests-by-section", main: () => <TestsBySection /> },
	{ path: "shift-report-config", main: () => <ShiftReportConfig /> },
	{ path: "heat-age-thresholds", main: () => <HeatAgeThresholds /> },
	{ path: "water-allocations", main: () => <WaterAllocations /> },
	{ path: "inspection-templates", main: () => <InspectionTemplates /> },
	{ path: "surface-defects", main: () => <SurfaceDefects /> },
	{ path: "water-meter-thresholds", main: () => <WaterMeterThresholds /> },
];

const components = routeComponents.map((route) => ({
	path: route.path,
	main: route.main,
}));

const sidebar = ({ option }: { option?: string }) => {
	const assetId = useAssetId();

	return (
		<>
			{sidebarSections.map((section) => {
				const visibleButtons = section.buttons.filter((button) =>
					isConfigurationAllowed(button.path, assetId),
				);

				if (visibleButtons.length === 0) return null;

				return (
					<List
						key={section.name}
						subheader={
							<ListSubheader
								sx={{
									backgroundColor: (theme) => theme.palette.background.paper,
									color: (theme) => theme.palette.primary.main,
									fontWeight: 600,
								}}
							>
								{section.name}
							</ListSubheader>
						}
					>
						{visibleButtons.map((button) => (
							<ListItemButton
								key={button.path}
								selected={option === button.path}
								component={Link}
								to={`${configurationPath}/${button.path}`}
							>
								<ListItemIcon>{button.icon}</ListItemIcon>
								<ListItemText primary={button.label} />
							</ListItemButton>
						))}
					</List>
				);
			})}
		</>
	);
};

const customConfigurations: any = [components, sidebar];

export { customConfigurations };
